import { FormEvent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { emailsApi, type EmailPreview, type EmailTemplatesIndex } from "../../api/emails";
import type { ApiError } from "../../api/client";
import { useToast } from "../../components/Toast";
import { Select } from "../../components/Select";

export default function AdminEmailPreviewPage() {
  const { t } = useTranslation();
  const toast = useToast();
  const [index, setIndex] = useState<EmailTemplatesIndex | null>(null);
  const [template, setTemplate] = useState<string>("INVITE");
  const [locale, setLocale] = useState<string>("en");
  const [modelText, setModelText] = useState<string>("");
  const [preview, setPreview] = useState<EmailPreview | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    void emailsApi
      .templates()
      .then((idx) => {
        setIndex(idx);
        // Seed with the default sample for the initially-selected template.
        const sample = idx.samples[template];
        if (sample) setModelText(JSON.stringify(sample, null, 2));
      })
      .catch((e: ApiError) => toast.error(e.message));
  }, []);

  // Reseed model textarea when the template changes (unless user has typed
  // something custom — we just always reseed for simplicity).
  useEffect(() => {
    if (!index) return;
    const sample = index.samples[template];
    setModelText(sample ? JSON.stringify(sample, null, 2) : "");
  }, [template, index]);

  async function render(e: FormEvent) {
    e.preventDefault();
    let model: Record<string, unknown> | null = null;
    if (modelText.trim()) {
      try {
        model = JSON.parse(modelText);
      } catch (parseErr) {
        toast.error(t("admin.emailPreview.error.invalidJson", { message: (parseErr as Error).message }));
        return;
      }
    }
    setLoading(true);
    try {
      setPreview(await emailsApi.preview(template, locale, model));
    } catch (err) {
      toast.error((err as ApiError).message);
    } finally {
      setLoading(false);
    }
  }

  if (!index) return <p style={{ color: "var(--ink-3)" }}>{t("admin.emailPreview.loadingTemplates")}</p>;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <h1 style={{ margin: 0, fontSize: 24, color: "var(--brand-ink)" }}>{t("admin.emailPreview.title")}</h1>
      <p style={{ color: "var(--ink-3)", fontSize: 13, margin: 0 }}>
        {t("admin.emailPreview.subtitle")}
      </p>

      <form onSubmit={render} style={card}>
        <div style={row}>
          <Field label={t("admin.emailPreview.field.template")}>
            <Select<string>
              value={template}
              onChange={setTemplate}
              ariaLabel={t("admin.emailPreview.field.template")}
              style={{ width: "100%" }}
              options={index.templates.map((tpl) => ({ value: tpl, label: tpl }))}
            />
          </Field>
          <Field label={t("admin.emailPreview.field.locale")}>
            <Select<string>
              value={locale}
              onChange={setLocale}
              ariaLabel={t("admin.emailPreview.field.locale")}
              style={{ width: "100%" }}
              options={index.locales.map((l) => ({ value: l, label: l }))}
            />
          </Field>
          <button type="submit" disabled={loading} style={primary}>
            {loading ? t("admin.emailPreview.action.rendering") : t("admin.emailPreview.action.render")}
          </button>
        </div>
        <Field label={t("admin.emailPreview.field.model")}>
          <textarea
            value={modelText}
            onChange={(e) => setModelText(e.target.value)}
            rows={10}
            style={{ ...input, fontFamily: "ui-monospace, monospace", fontSize: 12, resize: "vertical" }}
          />
        </Field>
      </form>

      {preview && (
        <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)", gap: 16 }}>
          <section style={card}>
            <h2 style={subTitle}>{t("admin.emailPreview.section.html")}</h2>
            <iframe
              title={t("admin.emailPreview.iframeTitle")}
              srcDoc={preview.html}
              style={{ width: "100%", minHeight: 480, border: "1px solid var(--border)", borderRadius: 6, background: "var(--surface)" }}
            />
          </section>
          <section style={card}>
            <h2 style={subTitle}>{t("admin.emailPreview.section.text")}</h2>
            <pre style={pre}>{preview.text}</pre>
          </section>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 13, flex: 1, minWidth: 160 }}>
      <span style={{ color: "var(--ink-3)" }}>{label}</span>
      {children}
    </label>
  );
}

const card: React.CSSProperties = { background: "var(--surface)", padding: 20, borderRadius: 10, boxShadow: "var(--shadow-1)", display: "flex", flexDirection: "column", gap: 12 };
const row: React.CSSProperties = { display: "flex", gap: 12, alignItems: "flex-end", flexWrap: "wrap" };
const input: React.CSSProperties = { padding: "8px 10px", borderRadius: 6, border: "1px solid var(--border-strong)", fontSize: 14, width: "100%" };
const primary: React.CSSProperties = { padding: "8px 16px", background: "var(--brand-primary-600)", color: "var(--on-primary)", border: "none", borderRadius: 6, fontSize: 14, cursor: "pointer", height: 36 };
const subTitle: React.CSSProperties = { margin: 0, fontSize: 12, color: "var(--ink-3)", textTransform: "uppercase", letterSpacing: 0.5 };
const pre: React.CSSProperties = { margin: 0, padding: 12, fontSize: 12, fontFamily: "ui-monospace, monospace", borderRadius: 6, background: "var(--surface-2)", overflow: "auto", maxHeight: 480, whiteSpace: "pre-wrap" };
